/**
 * Общая механика расписания для превью и guide-дорожки.
 *
 * Клип — это уже декодированный AudioBuffer, который должен звучать начиная
 * с абсолютного таймкода видео `startAt`. Расписание строится от
 * `video.currentTime`; на play/seek/pause/ratechange пересобирается заново.
 */

export const LOOKAHEAD = 0.06

export function dbToLinear(db: number): number {
  if (db <= -60) return 0
  return 10 ** (db / 20)
}

export type Clip = {
  buffer: AudioBuffer
  /** Абсолютный таймкод видео, на котором клип должен начаться. Может быть < 0. */
  startAt: number
  gainDb: number
}

export type ClipSchedulerOptions = {
  video: HTMLVideoElement
  getContext: () => AudioContext | null
  getClips: () => Clip[]
  /** Планировать ли вообще: выключенное превью ничего не ставит в очередь. */
  isActive: () => boolean
}

type Scheduled = { source: AudioBufferSourceNode; gain: GainNode }

export class ClipScheduler {
  private readonly options: ClipSchedulerOptions
  private readonly listeners: Array<[string, () => void]> = []
  private scheduled: Scheduled[] = []
  private disposed = false

  constructor(options: ClipSchedulerOptions) {
    this.options = options
    const resume = (): void => {
      // Граф врезан в <video> навсегда: если контекст усыплён, звук пропадёт
      // даже при выключенном превью. Поэтому будим его на каждом старте.
      const ctx = options.getContext()
      if (ctx && ctx.state === 'suspended') void ctx.resume().catch(() => undefined)
      this.reschedule()
    }
    const stop = (): void => this.stopAll()
    this.listeners.push(['play', resume])
    this.listeners.push(['playing', resume])
    this.listeners.push(['seeked', resume])
    this.listeners.push(['ratechange', resume])
    this.listeners.push(['pause', stop])
    this.listeners.push(['seeking', stop])
    this.listeners.push(['emptied', stop])
    for (const [event, handler] of this.listeners) {
      options.video.addEventListener(event, handler)
    }
  }

  reschedule(): void {
    if (this.disposed) return
    this.stopAll()
    if (!this.options.isActive()) return
    const ctx = this.options.getContext()
    if (!ctx) return
    const video = this.options.video
    if (video.paused || video.seeking) return
    if (ctx.state === 'suspended') void ctx.resume().catch(() => undefined)

    const rate = video.playbackRate || 1
    const videoNow = video.currentTime
    const ctxNow = ctx.currentTime + LOOKAHEAD
    // За время LOOKAHEAD видео уйдёт вперёд, поэтому моменту ctxNow соответствует
    // не текущий таймкод, а сдвинутый. Без этой поправки все клипы стабильно
    // опаздывают на LOOKAHEAD.
    const videoAtCtxNow = videoNow + LOOKAHEAD * rate

    for (const clip of this.options.getClips()) {
      const endAt = clip.startAt + clip.buffer.duration
      if (endAt <= videoNow) continue

      const gain = ctx.createGain()
      gain.gain.value = dbToLinear(clip.gainDb)
      const source = ctx.createBufferSource()
      source.buffer = clip.buffer
      source.playbackRate.value = rate
      source.connect(gain)
      gain.connect(ctx.destination)

      if (clip.startAt >= videoAtCtxNow) {
        source.start(ctxNow + (clip.startAt - videoAtCtxNow) / rate)
      } else {
        // Уже начался — вступаем с середины буфера (аналог atrim у ffmpeg).
        source.start(ctxNow, videoAtCtxNow - clip.startAt)
      }
      const entry: Scheduled = { source, gain }
      source.onended = (): void => {
        this.scheduled = this.scheduled.filter((s) => s !== entry)
        disconnect(entry)
      }
      this.scheduled.push(entry)
    }
  }

  stopAll(): void {
    for (const entry of this.scheduled) {
      try {
        entry.source.onended = null
        entry.source.stop()
      } catch {
        // уже остановлен
      }
      disconnect(entry)
    }
    this.scheduled = []
  }

  dispose(): void {
    if (this.disposed) return
    this.disposed = true
    this.stopAll()
    for (const [event, handler] of this.listeners) {
      this.options.video.removeEventListener(event, handler)
    }
    this.listeners.length = 0
  }
}

function disconnect(entry: Scheduled): void {
  try {
    entry.source.disconnect()
    entry.gain.disconnect()
  } catch {
    // уже отключён
  }
}
